'use client'

import {
  useGetCompanySearchQuery,
  useGetNewsSearchQuery,
  useGetProductSearchQuery,
} from '@/app/globalRedux/api/searchSlice'
import {
  addOrRemoveCompanySelectedActivity,
  setCompanyOrder,
  setCompanySelectedOption,
  setCultivationTypeCompany,
} from '@/app/globalRedux/filter/companyFilter'
import {
  addOrRemoveCriteria,
  setNewsOrder,
  setNewsSortSelectedOption,
} from '@/app/globalRedux/filter/newsFilter'
import {
  addOrRemoveFruitVegetableActivity,
  addOrRemoveFvProductFilterSelectedProduct,
  addOrRemovePpProductFilterSelectedProduct,
  addOrRemoveTlProductFilterSelectedProduct,
  Product,
  setAddOrRemoveAvailability,
  setCultivationType,
  setProductOrder,
  setProductSelectedSortOption,
} from '@/app/globalRedux/filter/productFilter'
import { RootState } from '@/app/globalRedux/store'
import useLocalizedRouter from '@/app/hooks/useLocalizedRouter'
import { useTranslation } from '@/app/i18n/client'
import { CompanyResponse, Country } from '@/app/types/companySearchType'
import { NewsSearchResponse } from '@/app/types/newsSearchType'
import { ProductSearchResponse } from '@/app/types/productSearchType'
import SearchResultSort from '@/components/SearchResultSelect'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { openModal } from '@/lib/filterMobileModalHandler'
import { convertNaNToOne } from '@/lib/formatUtils'
import { formatNumber } from '@/services/generalDataServices'
import Image from 'next/image'
import { useParams, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Filters from '../../Classses/Filters/Filters'
import NewsFilter from './(Filters)/NewsFilter'
import ProductFilter from './(Filters)/ProductFilter/ProductFilter'
import Tag from '@/components/ui/Tag'
import {
  addOrRemoveCompanyLocation,
  addOrRemoveCompanyLocationContinents,
  addOrRemoveProductLocation,
  addOrRemoveProductLocationContinents,
} from '@/app/globalRedux/filter/commonFilter'
import { ContinentType } from '@/app/types/constantDataType'
import Tooltip from '@mui/material/Tooltip'
import { usePageNumberUpdator } from '@/app/hooks/usePageParamUpdater'
import SwitchCompanyFilter from './(Filters)/CompanyFilters/SwitchCompanyFilter'
import { useFreeUserValidation } from '@/app/hooks/useFreeUserValidation'
import {
  setDoNotFetchForModal,
  setModalOpen,
} from '@/app/globalRedux/userStore/userStore'

const SearchResults = () => {
  const params = useParams()
  const dispatch = useDispatch()
  const pathName = usePathname()
  const { skipCompanyQuery, skipCompanyProductQuery, isModalApplicable } =
    useFreeUserValidation()
  const searchText = useSelector(
    (state: RootState) => state.searchData.searchTerm
  )
  const goToPage = usePageNumberUpdator()
  const companyFilter = useSelector((state: RootState) => state.companyFilter)
  const newsFilter = useSelector((state: RootState) => state.newsFilter)
  const productsFilter = useSelector((state: RootState) => state.productFilter)
  const commonFilter = useSelector((state: RootState) => state.commonFilter)

  const { data: companyDataProductBased } = useGetCompanySearchQuery<{
    data: CompanyResponse
    isFetching: boolean
  }>(
    {
      search_on: 'product',
      companyFilter: companyFilter,
      commonFilter: commonFilter,
      productFilter: productsFilter,
      lang: String(params?.lng),
      pageNo: Number(params?.companyPageNo?.[0] || 1),
    },
    {
      skip: companyFilter.isRouteUpdating || skipCompanyProductQuery(),
    }
  )

  const { data: companyDataNameBased } = useGetCompanySearchQuery<{
    data: CompanyResponse
    isFetching: boolean
  }>(
    {
      search_on: 'company_name',
      companyFilter: companyFilter,
      commonFilter: commonFilter,
      productFilter: productsFilter,
      lang: String(params?.lng),
      pageNo: convertNaNToOne(Number(params?.companyPageNo?.[0] || 1)),
    },
    {
      skip: companyFilter.isRouteUpdating || skipCompanyQuery(),
    }
  )

  const { data: productData } = useGetProductSearchQuery<{
    data: ProductSearchResponse
    isFetching: boolean
    isError: boolean
    isLoading: boolean
    isSuccess: boolean
  }>({
    productsFilter: productsFilter,
    commonFilter: commonFilter,
    pageNo: convertNaNToOne(Number(params?.productPageNO?.[0]) || 1),
  })

  const { data: newsData } = useGetNewsSearchQuery<{
    data: NewsSearchResponse
    isFetching: boolean
    isError: boolean
    isLoading: boolean
    isSuccess: boolean
  }>(
    {
      newsFilter: newsFilter,
      commonFilter: commonFilter,
      pageNO: convertNaNToOne(Number(params?.pageNO?.[0] ?? 1)),
    },
    {
      skip: newsFilter.isNewsRouteLoading,
    }
  )
  const modalHandler: {
    id: string
    component: JSX.Element
    tab: string
  }[] = [
    {
      id: 'company-modal',
      component: <SwitchCompanyFilter search="product" />,
      tab: 'PRODUCT_COMPANY_TAB',
    },
    {
      id: 'company-modal',
      component: <SwitchCompanyFilter search="company_name" />,
      tab: 'COMPANY_TAB',
    },
    {
      id: 'product-modal',
      component: <ProductFilter />,
      tab: 'PRODUCT_TAB',
    },
    {
      id: 'news-modal',
      component: <NewsFilter />,
      tab: 'NEWS_TAB',
    },
  ]

  const { t } = useTranslation()
  const pushWithLocale = useLocalizedRouter()
  const activeTab = Filters.getTab(pathName)
  const [currentMobileModal, setCurrentMobileModal] = useState<{
    id: string
    component: JSX.Element
    tab: string
  }>(modalHandler[0])

  useEffect(() => {
    const modal =
      modalHandler.find((item) => item.tab === Filters.getTab(pathName)) ??
      modalHandler[0]
    setCurrentMobileModal(modal)
  }, [pathName])

  const tabs = [
    {
      name: 'PRODUCT_COMPANY_TAB',
      count:
        companyDataProductBased?.display_count ||
        companyDataNameBased?.tab_count?.products,
      displayText: 'Products',
    },
    {
      name: 'COMPANY_TAB',
      count:
        companyDataNameBased?.display_count ||
        companyDataProductBased?.tab_count?.companies,
      displayText: 'Companies',
    },
    {
      name: 'PRODUCT_TAB',
      count: productData?.display_count,
      displayText: 'Offers',
    },
    { name: 'NEWS_TAB', count: newsData?.display_count, displayText: 'News' },
  ]
  const switchTabs = (tab: string) => {
    pushWithLocale(
      `/search/${Filters.getKeysByValue(tab)}?searchTerm=${searchText}`
    )
  }
  const handleCultivationCompanyChange = (cultivationType: string) => {
    dispatch(setCultivationTypeCompany(cultivationType))
    dispatch(setCultivationType(cultivationType))
  }
  const handleActivityChange = (fvSelectedActivity: string) => {
    dispatch(addOrRemoveCompanySelectedActivity(fvSelectedActivity))
    dispatch(addOrRemoveFruitVegetableActivity(fvSelectedActivity))
  }
  const handleFVSelectedChange = (productFilterFvSelectedProducts: Product) => {
    dispatch(
      addOrRemoveFvProductFilterSelectedProduct(productFilterFvSelectedProducts)
    )
  }
  const handlePPSelectedChange = (productFilterPpSelectedProducts: Product) => {
    dispatch(
      addOrRemovePpProductFilterSelectedProduct(productFilterPpSelectedProducts)
    )
  }
  const handleTLSelectedChange = (productFilterTlSelectedProducts: Product) => {
    dispatch(
      addOrRemoveTlProductFilterSelectedProduct(productFilterTlSelectedProducts)
    )
  }
  const handleAvailabilityChange = (availability: string) => {
    dispatch(setAddOrRemoveAvailability(availability))
  }
  const handleCompanyLocationChange = (companyLocation: Country) => {
    dispatch(addOrRemoveCompanyLocation(companyLocation))
  }
  const handleCompanyLocationContinentChange = (
    companyLocationContinents: ContinentType
  ) => {
    dispatch(addOrRemoveCompanyLocationContinents(companyLocationContinents))
  }
  const handleProductLocationChange = (productLocation: Country) => {
    dispatch(addOrRemoveProductLocation(productLocation))
  }
  const handleProductLocationContinentChange = (
    productLocationContinents: ContinentType
  ) => {
    dispatch(addOrRemoveProductLocationContinents(productLocationContinents))
  }
  const handleNewsCriteriaChange = (criteria: string) => {
    dispatch(addOrRemoveCriteria(criteria))
  }

  const mapFunctionTypes = (data: any, label: string, callback: any) =>
    data?.map((item: any) => ({
      value: item,
      label,
      callback,
    })) ?? []

  const productCompanyOfferTabFilter = (): any[] => {
    let Filter: any[] = []
    if (
      !(
        activeTab === 'PRODUCT_COMPANY_TAB' ||
        activeTab === 'COMPANY_TAB' ||
        activeTab === 'PRODUCT_TAB'
      )
    )
      return Filter

    Filter = [
      ...mapFunctionTypes(
        productsFilter?.productFilterFvSelectedProducts,
        'fv,',
        handleFVSelectedChange
      ),
      ...mapFunctionTypes(
        productsFilter.productFilterPpSelectedProducts,
        'pph,',
        handlePPSelectedChange
      ),
      ...mapFunctionTypes(
        productsFilter.productFilterTlSelectedProducts,
        'tl,',
        handleTLSelectedChange
      ),
    ]

    return Filter
  }

  const productCompanyTabFilter = () => {
    let Filter: any[] = []
    if (!(activeTab === 'PRODUCT_COMPANY_TAB' || activeTab === 'COMPANY_TAB'))
      return Filter

    Filter = [
      ...mapFunctionTypes(
        companyFilter?.cultivationType,
        'company_cultivation,',
        handleCultivationCompanyChange
      ),
      ...mapFunctionTypes(
        companyFilter?.fvSelectedActivity,
        'activity,',
        handleActivityChange
      ),
    ]
    return Filter
  }

  const productTabFilter = () => {
    let Filter: any[] = []
    if (!(activeTab === 'PRODUCT_TAB')) return Filter

    Filter = [
      ...mapFunctionTypes(
        productsFilter?.cultivationType,
        'company_cultivation,',
        handleCultivationCompanyChange
      ),
      ...mapFunctionTypes(
        productsFilter?.productFruitVegetableActivity,
        'activity,',
        handleActivityChange
      ),
      ...mapFunctionTypes(
        commonFilter?.productLocation,
        'product_location,',
        handleProductLocationChange
      ),
      ...mapFunctionTypes(
        commonFilter?.productLocationContients,
        'product_location_cont,',
        handleProductLocationContinentChange
      ),
      ...(productsFilter?.productFVAvailability
        ? [
            {
              value: productsFilter?.productFVAvailability,
              label: 'availability',
              callback: handleAvailabilityChange,
            },
          ]
        : []),
    ]
    return Filter
  }

  const newsTabFilter = () => {
    let Filter: any[] = []
    if (!(activeTab === 'NEWS_TAB')) return Filter
    Filter = [
      ...mapFunctionTypes(
        newsFilter?.criterias,
        'criterias,',
        handleNewsCriteriaChange
      ),
    ]
    return Filter
  }

  const comomonTabFilter = () => {
    const Filter = [
      ...mapFunctionTypes(
        commonFilter?.companyLocation,
        'location,',
        handleCompanyLocationChange
      ),
      ...mapFunctionTypes(
        commonFilter?.companyLocationContinents,
        'location,',
        handleCompanyLocationContinentChange
      ),
    ]
    return Filter
  }

  const filterItems = [
    ...productCompanyOfferTabFilter(),
    ...productCompanyTabFilter(),
    ...productTabFilter(),
    ...newsTabFilter(),
    ...comomonTabFilter(),
  ]

  const getAppliedFiltersTooltipList = (): string => {
    const appliedFiltersList = filterItems
      ?.slice(2, filterItems.length)
      ?.map((item) => (item.value?.name ? item.value?.name : item.value))

    return appliedFiltersList?.join(', ')
  }

  return (
    <div className="pb-5">
      <div className="flex flex-col justify-between align-center mobile:mt-4">
        <div className="flex text-grey_forty items-center gap-3 mobile-width mobile:justify-around mobile:overflow-x-auto mobile:pr-3">
          {tabs.map((tab) =>
            companyFilter?.sector === 'Retail Chains' &&
            ['PRODUCT_TAB'].includes(tab.name) ? (
              ''
            ) : (
              <Button
                key={tab.name}
                className={`py-0 px-1 pb-2 ${activeTab === tab.name ? 'text-secondary font-ns_bold rounded-none border-b-secondary border-b-2 ' : 'font-ns_regular'}`}
                variant="plain"
                size="rounded"
                onClick={() => {
                  switchTabs(tab.name)
                  if (isModalApplicable()) {
                    dispatch(setModalOpen(true))
                    dispatch(setDoNotFetchForModal(true))
                  }
                }}
              >
                {t(tab.displayText)}
                {tab?.count ? ` (${formatNumber(tab?.count)})` : null}
              </Button>
            )
          )}
        </div>
        <Separator />
        <div className="flex items-center justify-between mt-2 mobile-width">
          <div className="w-fit ">
            <div className="flex flex-row gap-[2px] mt-5 mobile:flex-col mobile:gap-[10px] mobile:mt-[10px]">
              {filterItems.length !== 0 && (
                <>
                  <div className="flex flex-row mr-[5px] items-center">
                    <p className="text-[#6f6f6f] text-sm font-medium text-nowrap ">
                      {t('Filters Applied')}
                    </p>
                  </div>
                  <div className="flex flex-row flex-wrap gap-2 w-full max-w-[500px] items-center">
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-nowrap flex flex-row flex-wrap items-center gap-2 ">
                      {filterItems.slice(0, 2).map((item: any) => (
                        <span
                          key={item.value}
                          className={`${isModalApplicable() ? 'cursor-not-allowed pointer-events-none opacity-50' : ''}`}
                        >
                          <Tag
                            label={t(
                              item.value?.name ? item.value?.name : item.value
                            )}
                            callBackFunction={() => {
                              item.callback(item.value)
                              goToPage({ newPageNo: 1 })
                            }}
                          />
                        </span>
                      ))}
                    </span>
                    {filterItems.length > 2 && (
                      <Tooltip
                        title={getAppliedFiltersTooltipList()}
                        arrow
                        placement="bottom-start"
                      >
                        <div className="text-[#fff] text-sm font-medium text-orange hover:cursor-default">
                          <p>{`+${filterItems.length - 2}`}</p>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="mobile:hidden w-[35%]">
            {!isModalApplicable() && <SortSearchResults />}
          </div>
          <div className="ml-auto mobile:block hidden">
            <Image
              onClick={() => openModal(currentMobileModal.id)}
              loading="lazy"
              width={30}
              height={30}
              src="/assets/Icons/mobile-filter-icon.svg"
              alt="filter-icon"
              className="border-[#ddd] border-solid bg-[#F4F4F4] px-[5px] py-[5px] rounded"
            />
            <div
              id={currentMobileModal.id}
              className="modal-hidden fixed top-0 left-0 z-50 w-full h-full mobile:flex items-center justify-center bg-gray-900 bg-opacity-50 overflow-y-hidden"
            >
              <div className="bg-white rounded-lg overflow-y-auto shadow-xl transform transition-all sm:max-w-lg sm:w-full mobile:h-[80vh] mobile-width mobile:py-[10px] mobile:px-[10px] p-5">
                {currentMobileModal.component}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchResults

const SortSearchResults = () => {
  const pathName = usePathname()
  const dispatch = useDispatch()
  const { order: companyOrder, companySelectedSortOption } = useSelector(
    (state: RootState) => state.companyFilter
  )
  const { order: productOrder, productSelectedSortOption } = useSelector(
    (state: RootState) => state.productFilter
  )
  const { order: newsOrder, selectedNewsSortOption } = useSelector(
    (state: RootState) => state.newsFilter
  )

  if (Filters.getTab(pathName).includes('COMPANY_TAB')) {
    return (
      <SearchResultSort
        options="companySort"
        selectedOption={companySelectedSortOption}
        sortByFunction={(value) => {
          dispatch(setCompanySelectedOption(value))
        }}
        order={companyOrder}
        orderByFunction={(value) => {
          dispatch(setCompanyOrder(value))
        }}
      />
    )
  }

  if (Filters.getTab(pathName) === 'PRODUCT_TAB') {
    return (
      <SearchResultSort
        options="productSort"
        selectedOption={productSelectedSortOption}
        sortByFunction={(value) => {
          dispatch(setProductSelectedSortOption(value))
        }}
        order={productOrder}
        orderByFunction={(value) => {
          dispatch(setProductOrder(value))
        }}
      />
    )
  }
  if (Filters.getTab(pathName) === 'NEWS_TAB') {
    return (
      <SearchResultSort
        options="NewsSort"
        selectedOption={selectedNewsSortOption}
        sortByFunction={(value) => {
          dispatch(setNewsSortSelectedOption(value))
        }}
        order={newsOrder}
        orderByFunction={(value) => {
          dispatch(setNewsOrder(value))
        }}
      />
    )
  }
}
